// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PieChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import DefaultChartOptionsBuilder from './Izenda.DefaultChartOptionsBuilder';
import jsFormatService from 'share/Izenda.JsFormatService';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
import { size, omit, merge, assign, get, isArray, filter, flow, has, find, isNil, isNull, isDate } from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { isChartType } from 'logic/data/Izenda.ReportPartChartConstants';

const showText = HighchartUtils.showText;

@exportClass('PieChartOptionsBuilder')
export default class PieChartOptionsBuilder extends DefaultChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    let chartOptions = super.buildOptionsByType(visualType, userOptions, dataParser);

    const izendaOptions = userOptions.izendaOptions || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const isShowDataLabels =
      izendaOptions.showValueLabel || izendaOptions.showSliceLabel || izendaOptions.showPercentage || false;
    const chartSizePercent = 100;
    const chartInnerSizePercent = Math.round(chartSizePercent / 2);

    const xAxis = chartOptions.xAxis;
    const xAxisName = xAxis.axisName;

    if (userOptions.series) {
      chartOptions = omit('series', chartOptions);
    }

    userOptions.series = removeNegativeData(userOptions.series || chartOptions.series);

    if (izendaOptions.bottomXpercent) {
      applyBottomPercent(izendaOptions);
    }

    chartOptions = merge(chartOptions, {
      chart: {
        type: 'pie'
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: isShowDataLabels,
            formatter: getDataLabelFormat,
            useHTML: true
          }
        },
        pie: {
          showInLegend: true,
          size: `${chartSizePercent}%`,
          dataLabels: {
            padding: 0
          }
        }
      }
    });

    if (izendaOptions.showPercentage) {
      chartOptions = assign(chartOptions, {
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
        }
      });
    }

    // add sub-report to x-axis
    !(userOptions.izMultiLevel && isChartType(this.chartOptions.chartType, CHART_TYPE.TreeMap))
      && HighchartUtils.addPointActionLink(
        true,
        userOptions,
        get('plotOptions.series.dataLabels', chartOptions),
        xAxisName,
        get(xAxisName, izendaSeriesConfig)
      );

    if (izendaOptions.pieChartStyle === 'ring') {
      return $izenda.extend(true, chartOptions, {
        plotOptions: {
          pie: {
            innerSize: chartInnerSizePercent + '%'
          }
        }
      });
    } else if (izendaOptions.pieChartStyle === 'semiCirle') {
      return $izenda.extend(true, chartOptions, {
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 90,
            center: ['50%', '80%'],
            innerSize: chartInnerSizePercent + '%'
          }
        }
      });
    }

    return chartOptions;

    function removeNegativeData(series) {
      if (isArray(series)) {
        return series.filter(serie => {
          serie.data = filter(data => {
            data.value = data.y;
            return get('y', data) > 0;
          }, serie.data);

          return size(serie.data);
        });
      } else {
        return series;
      }
    }

    function getDataLabelFormat() {
      const point = this.point;
      const pointOpts = get('options', point);
      const dataLabel = [];
      const userOptions = this.series.userOptions || {};

      const pointFormatedName = find(val => !isNil(val), [pointOpts.key, pointOpts.name, point.x]);
      let pointName = find(val => !isNil(val), [pointOpts.key, pointOpts.originalNameValue, pointOpts.name, point.x]);

      // For null value , just show 'null' text on chart
      isNull(pointOpts.name) && (pointName = 'null');

      let customFormat = getCustomFormat(pointOpts, userOptions.yAxisField);
      const labelNameConfig = izendaSeriesConfig[userOptions.labelName] || izendaSeriesConfig[point.labelName];
      const dataType = get('fieldElement.properties.dataFormattings.functionInfo.formatDataType', labelNameConfig);

      const jsFormatStringLabelName = get(
        'fieldElement.properties.dataFormattings.format.jsFormatString',
        labelNameConfig
      );

      let sliceLabel;
      if (jsFormatStringLabelName) {
        let pointData;
        if (isNil(pointName)) {
          pointData = null;
        } else {
          if (isDate(pointName) && dataType === 'Datetime') {
            pointData = new Date(pointName);
          } else {
            pointData = pointName;
          }
        }

        const jsFormatId = get('fieldElement.properties.dataFormattings.format.formatId', labelNameConfig);

        sliceLabel = jsFormatService.format(jsFormatId, pointData, pointName);
      } else {
        sliceLabel = dataParser.getFormatData({
          value: pointFormatedName,
          originalValue: pointName
        }, labelNameConfig);
      }
      !!izendaOptions.showSliceLabel &&
        dataLabel.push(String.format('<span class="point-label">{0}</span>', showText(sliceLabel)));

      // data label
      let valueLabel;
      const nameConfig = izendaSeriesConfig[userOptions.name] || izendaSeriesConfig[point.name];
      const jsFormatString = get('fieldElement.properties.dataFormattings.format.jsFormatString', nameConfig);

      const jsFormatId = get('fieldElement.properties.dataFormattings.format.formatId', nameConfig);

      if (jsFormatString) {
        customFormat = jsFormatService.format(jsFormatId, pointOpts.y || this.point.value);
      }

      const fieldValue = {
        value: customFormat || pointOpts.y || this.point.value,
        originalValue: pointOpts.y || this.point.value
      };

      valueLabel = dataParser.getFormatData(fieldValue, nameConfig);

      !!izendaOptions.showValueLabel &&
        dataLabel.push(String.format('<span class="point-value">  {0}</span>', showText(valueLabel)));

      // percentage
      !!izendaOptions.showPercentage &&
        dataLabel.push(String.format('<span class="point-percent">  ({0}%)</span>', this.percentage.toFixed(2)));

      return String.format('<span class="pie-serie-label">{0}</span>', dataLabel.join(' '));
    }

    // Get custom format for data label
    function getCustomFormat(pointOpts, yAxisField) {
      let result = null;
      const columnName = get('columnName', yAxisField);
      const hasCustomFormat = flow(
        get('record'),
        has(constants.CUSTOM_FORMAT_PREFIX + columnName)
      )(pointOpts);

      if (hasCustomFormat) {
        result = flow(
          get('record'),
          get(columnName)
        )(pointOpts);
      }

      return result;
    }

    function applyBottomPercent(izendaOptions) {
      const series = userOptions.series || chartOptions.series;
      const data = get('[0].data', series);

      if (data) {
        series[0].data = get(
          'newData',
          ChartDataParser.processDataByBottomPercent(izendaOptions.bottomXpercent, data, 'name', 'y')
        );
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.PieChartOptionsBuilder.js